/**
 * @module input/dwellClick
 * Dwell-to-select: activates a UI target after the pointer hovers over it
 * for a configured duration (CONFIG.dwell.holdDuration) without leaving the zone.
 *
 * This is the sole hand-tracking navigation mechanism in non-PLAYING states.
 * Physical click/tap events are always wired in parallel as a fallback.
 *
 * ADR-003: Only hand[0] drives dwell (hand[1] is ignored here to prevent the
 * second hand from accidentally selecting menu items).
 *
 * ADR-003: updateDwell() returns {targetId:null, progress:0} when called
 * during PLAYING state — the state machine gate is enforced by the caller
 * (GameLoop / main.ts) checking StateMachine.getCurrentState().
 */

import type { Vec2 } from '../types.js';
import { CONFIG } from '../game-config.js';

// ---------------------------------------------------------------------------
// Types (per module-contracts.md)
// ---------------------------------------------------------------------------

export interface DwellTarget {
  id: string;
  /** Centre of the button in canvas coordinates. */
  center: Vec2;
  /** Radius of the hover detection zone. */
  radius: number;
  /** Called synchronously when dwell completes. */
  onActivate: () => void;
}

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

const _targets: DwellTarget[] = [];

let _activeId: string | null = null;
let _activeStartMs: number | null = null;

// ---------------------------------------------------------------------------
// Public functional API (per module-contracts.md)
// ---------------------------------------------------------------------------

/**
 * Register a UI target for dwell interaction.
 * Returns an unregister function — call it when the target is removed from the UI.
 */
export function registerTarget(target: DwellTarget): () => void {
  _targets.push(target);
  return () => {
    const idx = _targets.indexOf(target);
    if (idx !== -1) _targets.splice(idx, 1);
    // If we were dwelling on the removed target, reset state.
    if (_activeId === target.id) {
      _activeId = null;
      _activeStartMs = null;
    }
  };
}

/**
 * Update dwell state from the current hand[0] pointer position.
 * Call once per tracking frame when state !== 'PLAYING'.
 *
 * `pointerPos`: smoothed position of hand[0]; null = no hand detected.
 * `deltaMs`: milliseconds since the last call (used to accumulate hold time).
 *
 * Returns progress [0.0–1.0] for the currently hovered target (for ring draw).
 * Fires `onActivate()` synchronously when progress reaches 1.0.
 */
export function updateDwell(
  pointerPos: Vec2 | null,
  deltaMs: number,
): { targetId: string | null; progress: number } {
  void deltaMs; // dwell uses wall-clock accumulation via absolute timestamps

  if (!pointerPos) {
    _resetActive();
    return { targetId: null, progress: 0 };
  }

  const nowMs = performance.now();

  // Find the first target the pointer is inside.
  const hit = _targets.find((t) => {
    const dx = pointerPos.x - t.center.x;
    const dy = pointerPos.y - t.center.y;
    return dx * dx + dy * dy <= t.radius * t.radius;
  });

  if (!hit) {
    _resetActive();
    return { targetId: null, progress: 0 };
  }

  // Changed target — restart accumulation.
  if (_activeId !== hit.id) {
    _activeId = hit.id;
    _activeStartMs = nowMs;
    return { targetId: hit.id, progress: 0 };
  }

  // Still on the same target — compute progress.
  if (_activeStartMs === null) {
    _activeStartMs = nowMs;
  }

  const elapsed = nowMs - _activeStartMs;
  const progress = Math.min(elapsed / CONFIG.dwell.holdDuration, 1);

  if (progress >= 1) {
    // Dwell complete — fire and reset so it does not re-fire continuously.
    _resetActive();
    hit.onActivate();
    return { targetId: hit.id, progress: 1 };
  }

  return { targetId: hit.id, progress };
}

/** Clear all dwell state and all registered targets (call on state transition). */
export function resetDwell(): void {
  _targets.length = 0;
  _resetActive();
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _resetActive(): void {
  _activeId = null;
  _activeStartMs = null;
}

// ---------------------------------------------------------------------------
// Legacy class-based API (backwards compat with main.ts PoC)
// ---------------------------------------------------------------------------

/** Represents a rectangular dwell target (legacy API). */
export interface DwellTargetRect {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Emitted when a dwell completes on a target (legacy API). */
export interface DwellEvent {
  targetId: string;
}

/**
 * @deprecated Use the functional API (registerTarget, updateDwell, resetDwell).
 * Kept for backwards compat with the PoC main.ts.
 */
export class DwellClickManager {
  private _targets: DwellTargetRect[] = [];
  private _activeId: string | null = null;
  private _startTime: number | null = null;

  setTargets(targets: DwellTargetRect[]): void {
    this._targets = targets;
    this._reset();
  }

  update(
    pos: { x: number; y: number },
    timestamp: number,
    holdDuration: number,
  ): DwellEvent | null {
    const hit = this._targets.find(
      (t) =>
        pos.x >= t.x &&
        pos.x <= t.x + t.width &&
        pos.y >= t.y &&
        pos.y <= t.y + t.height,
    );

    if (!hit) {
      this._reset();
      return null;
    }

    if (this._activeId !== hit.id) {
      this._activeId = hit.id;
      this._startTime = timestamp;
      return null;
    }

    if (this._startTime !== null && timestamp - this._startTime >= holdDuration) {
      const targetId = hit.id;
      this._reset();
      return { targetId };
    }

    return null;
  }

  progress(timestamp: number, holdDuration: number): number {
    if (this._activeId === null || this._startTime === null) return 0;
    return Math.min((timestamp - this._startTime) / holdDuration, 1);
  }

  private _reset(): void {
    this._activeId = null;
    this._startTime = null;
  }
}
